import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import {
    Button,
    Cascader,
    Checkbox,
    Col,
    DatePicker,
    Divider,
    InputNumber,
    Popconfirm,
    Row,
    Select,
    Slider,
    Space,
    Tooltip,
} from "antd";
import { event, lib } from "@/index";
import moment from "moment";
import { inject as _inject, observer, Provider } from "mobx-react";
import { CloseOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import "./index.less";
import { useHistory } from "react-router-dom";
import { DTInput } from "@dt/components";
import _ from "lodash";
import { SearchSourceType } from "./search-constant";
import { SearchStore } from "./search-store";

import SearchItemRoot from "./search-item-root";
import classNames from "classnames";
import { useMountedOrUpdateEffectFn } from "@dt/hooks";
var inject = fn => _inject("store")(observer(fn));

function getSearchParam(url) {
    if (url.indexOf("?") == -1) {
        return null;
    }
    let search = url.split("?")[1];
    let result = {},
        ps = search.split("&");
    ps.map(item => {
        let [key, value] = item.split("=");
        result[key] = decodeURIComponent(value);
    });
    return result;
}

const depFn = (arr, result, fn, valueKey) => {
    for (let i = 0; i < arr.length; i++) {
        const cache = [...result];
        cache.push(arr[i][valueKey]);
        if (fn(arr[i], cache)) {
            continue;
        }
        if (Array.isArray(arr[i].children) && arr[i].children.length > 0) {
            depFn(arr[i].children, cache, fn, valueKey);
        }
    }
};
const ShowTooltip = props => {
    const { item } = props;
    if (item?.tooltip) {
        return (
            <Tooltip title={item.tooltip}>
                &nbsp;
                <ExclamationCircleOutlined />
            </Tooltip>
        );
    }
    return null;
};

var InputForm = inject(({ store, item, searchFc }) => {
    let search = store.assembleSearch();
    const { isTextarea, key } = item;
    const valueTypeIsString = !item.valueType || item.valueType === "string";
    const valueTypeIsArray = item.valueType === "array";
    const onChange = e => {
        let inputValue = e.target.value;
        if (isTextarea) {
            if (valueTypeIsString) {
                if (inputValue) {
                    store.setValue(key, inputValue.split("\n").join(","));
                } else {
                    store.setValue(key, "");
                }
            } else if (valueTypeIsArray) {
                if (inputValue) {
                    store.setValue(key, inputValue.split("\n"));
                } else {
                    store.setValue(key, []);
                }
            }
        }
    };
    const showValue = useMemo(() => {
        if (isTextarea) {
            if (valueTypeIsString) {
                return search[item.key]?.split(",").join("\n");
            } else if (valueTypeIsArray) {
                return search[item.key]?.join("\n");
            }
        }
        return "";
    }, [search[item.key], isTextarea]);

    return (
        <SearchItemRoot>
            <label className={classNames("search-item-label", { "require-label": item.required })}>
                {item.label}
                <ShowTooltip item={item} />
            </label>
            {item.isTextarea ? (
                <div className="search-item-control">
                    <DTInput.TextArea
                        trimMode={item.trimMode}
                        style={{ marginLeft: 0 }}
                        autoSize={{ minRows: 1, maxRows: 6 }}
                        placeholder={item.placeholder || `多行输入`}
                        value={showValue}
                        onChange={onChange}
                    />
                </div>
            ) : (
                <div className="search-item-control">
                    <DTInput
                        trimMode={item.trimMode}
                        value={search[item.key]}
                        placeholder={item.placeholder || `请输入${item.label}`}
                        onChange={e => store.setValue(item.key, e.target.value)}
                        onPressEnter={searchFc}
                    />
                </div>
            )}
        </SearchItemRoot>
    );
});

var SelectForm = inject(({ store, item, renderLabel, onChangeKey }) => {
    let search = store.assembleSearch();
    const isMultiSelect = item.isMultiSelect || item.type === "select-check";
    let [list, setList] = useState([]);
    /**
     * 搜索的内容
     */
    let [searchText, setSearchText] = useState("");
    const [checkAllState, setCheckAllState] = useState({ checked: false, indeterminate: false });
    let valueKey = item.valueKey || "id" || "value";
    let labelKey = item.labelKey || "name" || "label";
    useEffect(() => {
        if (item.source == "json") {
            try {
                setList(eval(item.json));
            } catch (e) {
                console.error(`${item.label}解析出错了，请检查该JSON`, e);
            }
        } else {
            let url = item.api.split("?")[0];
            let data = getSearchParam(item.api);
            lib.request({
                url,
                data,
                needMask: false,
                success: list => setList(list || []),
            });
        }
    }, [item]);

    const checkAll = e => {
        let checked = e.target.checked;
        setCheckAllState({ checked: checked, indeterminate: false });
        let value;
        if (checked) {
            //有搜索内容需要按内容过滤
            if (searchText) {
                const filter = list.filter(item =>
                    item[labelKey].toLowerCase().includes(searchText.toLowerCase().trim()),
                );
                const filterValue = filter.map(item => item[valueKey]);
                value = _.uniq((search[item.key] || []).concat(filterValue));
            } else {
                value = list.map(item => item[valueKey]);
            }
        } else {
            value = [];
        }
        store.setValue(item.key, value);
    };

    const handleChange = value => {
        onChangeKey && onChangeKey(value);
        checkSelectAll(value);
        store.setValue(item.key, value);
    };

    const maxTagPlaceholder = omittedValues => {
        const title = <span>{`+${omittedValues.length}...`}</span>;
        const labels = omittedValues.map(item => item.label).join(",");
        return <Tooltip title={labels}>{title}</Tooltip>;
    };
    /**
     * 自定义选择标签
     * @param props  CustomTagProps
     * @returns {JSX.Element}
     */
    const tagRender = props => {
        const { label, value, closable, onClose } = props;
        const onPreventMouseDown = event => {
            event.preventDefault();
            event.stopPropagation();
        };
        return (
            <span className="ant-select-selection-item">
                <span className="ant-select-selection-item-content">{Array.isArray(label) ? label[1] : label}</span>
                <span
                    className="ant-select-selection-item-remove"
                    style={{ userSelect: "none", verticalAlign: "0" }}
                    onMouseDown={onPreventMouseDown}
                    onClick={() => {
                        onClose();
                    }}>
                    <CloseOutlined />
                </span>
            </span>
        );
    };
    function checkSelectAll(selectedValue) {
        if (isMultiSelect) {
            let checked = selectedValue.length === list.length;
            setCheckAllState({ checked: checked, indeterminate: selectedValue.length !== 0 && !checked });
        }
    }
    function checkSelectAllByText(text) {
        if (text) {
            let fitterText = text.trim();
            const filter = list.filter(item => {
                item[labelKey].toLowerCase().includes(fitterText.toLowerCase());
            });
            const filterValue = filter.map(item => item[valueKey]);
            // 使用 _.intersection 获取两个数组的交集 查找按下拉搜索输入文字过滤选中的列表
            const intersection = _.intersection(filterValue, search[item.key] || []);
            checkSelectAll(intersection);
        } else {
            const value = search[item.key] || [];
            checkSelectAll(value);
        }
    }

    return (
        <SearchItemRoot>
            {renderLabel ? (
                renderLabel()
            ) : (
                <label className={classNames("search-item-label", { "require-label": item.required })}>
                    {item.label}
                    <ShowTooltip item={item} />
                </label>
            )}
            <div className="search-item-control">
                <Select
                    allowClear
                    placeholder={item.placeholder || `请选择${item?.label?.replace?.("：", "")}`}
                    value={search[item.key]}
                    onChange={handleChange}
                    showSearch={item.isSearch}
                    filterOption={(input, option) => {
                        return option.data[labelKey].toLowerCase().indexOf(input.toLowerCase().trim()) >= 0;
                    }}
                    maxTagCount={1}
                    searchValue={searchText}
                    onSearch={value => {
                        setSearchText(value);
                        checkSelectAllByText(value);
                    }}
                    mode={isMultiSelect ? "multiple" : null}
                    tagRender={tagRender}
                    menuItemSelectedIcon={null}
                    dropdownRender={menu => {
                        return (
                            <>
                                {isMultiSelect && (
                                    <div className={"ant-select-item"}>
                                        <Checkbox
                                            onChange={checkAll}
                                            checked={checkAllState.checked}
                                            indeterminate={checkAllState.indeterminate}>
                                            全选
                                        </Checkbox>
                                        <Divider style={{ margin: "5px 0" }} />
                                    </div>
                                )}
                                {menu}
                            </>
                        );
                    }}
                    onDropdownVisibleChange={open => {
                        if (!open) {
                            setSearchText("");
                            checkSelectAllByText("");
                        }
                    }}>
                    {list.map((optItem, index) => (
                        <Select.Option key={index} value={optItem[valueKey]} title={optItem[labelKey]} data={optItem}>
                            {isMultiSelect ? (
                                <Checkbox
                                    checked={search[item.key]?.includes(optItem[valueKey])}
                                    style={{ marginRight: 6 }}>
                                    {optItem[labelKey]}
                                </Checkbox>
                            ) : (
                                optItem[labelKey]
                            )}
                        </Select.Option>
                    ))}
                </Select>
            </div>
        </SearchItemRoot>
    );
});

var MultiSelectForm = inject(({ store, item }) => {
    const { search } = store;
    const [list, setList] = useState([]);
    const [selectValue, setSelectValue] = useState("");
    let { key1, key2 } = item;

    useEffect(() => {
        if (!item.keySource || item.keySource == "json") {
            try {
                const arr = eval(item.keyJson);
                setList(arr);
                if (arr.length > 0) {
                    const defaultId = arr[0].id;
                    setSelectValue(defaultId);
                    if (search[key2]) {
                        store.setValue(key1, defaultId);
                    }
                } else {
                    console.error(`json数据请至少添加一项`);
                }
            } catch (e) {
                console.error(`解析出错了，请检查该JSON`, e);
            }
        } else {
            const data = getParams();
            let url = item.keyApi.split("?")[0];

            lib.request({
                url,
                data,
                needMask: false,
                success: list => setList(list || []),
                fail: () => {},
            });
        }
    }, []);

    const getParams = () => {
        let result = getSearchParam(item.keyApi) || {};
        return result;
    };

    return (
        <>
            <SelectForm
                store={store}
                item={{ ...item, key: key2 }}
                renderLabel={() => {
                    return (
                        <Select
                            value={selectValue}
                            onChange={value => {
                                if (search[key2]) {
                                    store.setValue(key1, value);
                                }
                                setSelectValue(value);
                            }}
                            size={item.size}
                            style={{ width: "120px", marginRight: "10px" }}>
                            {list.map((item, index) => (
                                <Select.Option key={index} value={item.id}>
                                    {item.name}
                                </Select.Option>
                            ))}
                        </Select>
                    );
                }}
                onChangeKey={value => {
                    console.log("value:", value, selectValue);
                    if (!value || (Array.isArray(value) && value.length === 0)) {
                        store.setValue(key1, "");
                    } else {
                        store.setValue(key1, selectValue);
                    }
                }}
            />
        </>
    );
});

var DateForm = inject(({ store, item }) => {
    let search = store.assembleSearch();
    let { start, end, key, needDefault } = item;
    let userAgent = navigator.userAgent;
    let isSafari = userAgent.indexOf("Safari") > -1 && userAgent.indexOf("Chrome") == -1;
    const renderFormat = () => {
        return isSafari ? "YYYY/MM/DD HH:mm:ss" : "YYYY-MM-DD HH:mm:ss";
    };
    if (!item.isRange) {
        return (
            <div className="search-item">
                <label className={classNames("search-item-label", { "require-label": item.required })}>
                    {item.label}
                    <ShowTooltip item={item} />
                </label>
                <div className="search-item-control">
                    <DatePicker
                        value={search[key] ? moment(parseInt(search[key])) : ""}
                        onChange={(e, dates) => {
                            store.setValue(key, new Date(dates).getTime() - 8 * 3600 * 1000);
                        }}
                        picker={item.dateType}
                    />
                </div>
            </div>
        );
    } else {
        const defaultRangeValue = [];
        if (needDefault) {
            defaultRangeValue.push(moment(new Date().getTime()));
            const currentDate = new Date();
            const day = +item.defaultDays || 7;
            currentDate.setDate(currentDate.getDate() + day);
            defaultRangeValue.push(moment(currentDate.getTime()));
        }
        return (
            <SearchItemRoot>
                <label className={classNames("search-item-label", { "require-label": item.required })}>
                    {item.label}
                    <ShowTooltip item={item} />
                </label>
                <div className="search-item-control">
                    <DatePicker.RangePicker
                        //   value={search[start] ? [moment(parseInt(search[start])), moment(parseInt(search[end]))] : defaultRangeValue}
                        value={
                            search[start] || search[end]
                                ? [
                                      search[start] ? moment(parseInt(search[start])) : null,
                                      search[end] ? moment(parseInt(search[end])) : null,
                                  ]
                                : defaultRangeValue
                        }
                        showTime={
                            item.dateType == "second"
                                ? {
                                      hideDisabledOptions: true,
                                      defaultValue: [moment("00:00:00", "HH:mm:ss"), moment("23:59:59", "HH:mm:ss")],
                                  }
                                : false
                        }
                        format={item.dateType == "second" ? renderFormat() : false}
                        onCalendarChange={(dates, dateStrings, info) => {
                            if (dateStrings[0]) {
                                item.dateType == "second"
                                    ? store.setValue(start, new Date(dateStrings[0]).getTime())
                                    : store.setValue(start, new Date(dateStrings[0]).getTime() - 8 * 3600 * 1000);
                            } else {
                                store.setValue(start, "");
                            }
                            if (dateStrings[1]) {
                                item.dateType == "second"
                                    ? store.setValue(end, new Date(dateStrings[1]).getTime())
                                    : store.setValue(end, new Date(dateStrings[1]).getTime() + 16 * 3600 * 1000 - 1);
                            } else {
                                store.setValue(end, "");
                            }
                        }}
                        //   onChange={(e, dates) => {
                        //     if (!dates[0]) {
                        //       store.setValue(start, "");
                        //       store.setValue(end, "");
                        //     } else {
                        //       if (item.dateType == "second") {
                        //         store.setValue(start, new Date(dates[0]).getTime());
                        //         store.setValue(end, new Date(dates[1]).getTime());
                        //       } else {
                        //         store.setValue(start, new Date(dates[0]).getTime() - 8 * 3600 * 1000);
                        //         store.setValue(end, new Date(dates[1]).getTime() + 16 * 3600 * 1000 - 1);
                        //       }
                        //     }
                        //   }}
                        picker={item.dateType == "second" ? "date" : item.dateType}
                    />
                </div>
            </SearchItemRoot>
        );
    }
});

var SelectTextArea = inject(({ store, item }) => {
    let search = store.assembleSearch();
    let [list, setList] = useState([]);
    let [selectValue, setSelectValue] = useState("");
    let { key1, key2 } = item;
    useEffect(() => {
        list = eval(item.json);
        setList(list);
        setSelectValue(list[0]?.id);
    }, []);
    useEffect(() => {
        search[key1] && setSelectValue(search[key1]);
    }, [search[key1]]);
    return (
        <SearchItemRoot>
            <div className=" ">
                <Select
                    value={selectValue}
                    onChange={value => {
                        if (search[key2]) {
                            store.setValue(key1, value);
                        }
                        setSelectValue(value);
                    }}
                    className="search-select">
                    {list.map((item, index) => (
                        <Select.Option key={index} value={item.id}>
                            {item.name}
                        </Select.Option>
                    ))}
                </Select>
            </div>
            <div className="search-item-control">
                <DTInput.TextArea
                    trimMode={item.trimMode}
                    autoSize={{ minRows: 1, maxRows: 6 }}
                    placeholder="每条数据占一行"
                    value={search[key2]?.split(",").join("\n")}
                    onChange={e => {
                        let value = e.target.value;
                        if (value && selectValue) {
                            store.setValue(key1, selectValue);
                            store.setValue(key2, value.split("\n").join(","));
                        } else {
                            store.setValue(key1, "");
                            store.setValue(key2, "");
                        }
                    }}
                />
            </div>
        </SearchItemRoot>
    );
});

var CascaderForm = inject(({ store, item }) => {
    let valueKey = item.valueKey || "id" || "value";
    let labelKey = item.labelKey || "name" || "label";
    item.separator = "";
    let search = store.assembleSearch();
    let [list, setList] = useState([]);
    useEffect(() => {
        if (item.source == "json") {
            try {
                setList(eval(item.json));
            } catch (e) {
                console.error(`${item.label}解析出错了，请检查该JSON`, e);
            }
        } else {
            let url = item.api.split("?")[0];
            let data = getSearchParam(item.api);
            lib.request({
                url,
                data,
                needMask: false,
                success: list => setList(list || []),
            });
        }
    }, []);

    const simpleDataToCascaderData = data => {
        if (!Array.isArray(data)) return null;
        const results = [];
        depFn(
            list,
            [],
            (obj, arrs) => {
                if (data.includes(obj[valueKey])) {
                    results.push(arrs);
                    return true;
                }
                return false;
            },
            valueKey,
        );
        return results;
    };

    return (
        <SearchItemRoot>
            <label className={classNames("search-item-label", { "require-label": item.required })}>
                {item.label}
                <ShowTooltip item={item} />
            </label>
            <div className="search-item-control">
                <Cascader
                    fieldNames={{
                        label: labelKey,
                        value: valueKey,
                        children: "children",
                    }}
                    multiple={item.isMultiSelect}
                    options={list}
                    value={
                        item.isMultiSelect ? simpleDataToCascaderData(search[item.key]) : search[item.key]?.split(",")
                    }
                    onChange={values => {
                        if (item.isMultiSelect) {
                            const arr = [];
                            values.map((obj, index) => {
                                arr.push(obj[obj.length - 1]);
                            });
                            store.setValue(item.key, arr);
                        } else {
                            store.setValue(item.key, values?.join(","));
                        }
                    }}
                />
            </div>
        </SearchItemRoot>
    );
});

const SelectFormCheck = inject(({ store, item }) => {
    let [list, setList] = useState([]);
    let [value, valueSet] = useState([]),
        [allId, allIdSet] = useState([]),
        [allChecked, allCheckedSet] = useState({ checked: false, indeterminate: false });
    useEffect(() => {
        if (item.source == "json") {
            try {
                setList(eval(item.json));
                allIdSet(eval(item.json).reduce((prev, curr) => [...prev, curr.id], []));
            } catch (e) {
                console.error(`${item.label}解析出错了，请检查该JSON`, e);
            }
        } else {
            let url = item.api.split("?")[0];
            let data = getSearchParam(item.api);
            lib.request({
                url,
                data,
                needMask: false,
                success: list => {
                    setList(list || []);
                    allIdSet((list || []).reduce((prev, curr) => [...prev, curr.id], []));
                },
            });
        }
    }, []);

    function changeSelect(value) {
        store.setValue(item.key, value);
        valueSet(value);
        if (value.length == allId.length) {
            allCheckedSet({ checked: true, indeterminate: false });
        } else if (value.length == 0) {
            allCheckedSet({ checked: false, indeterminate: false });
        } else {
            allCheckedSet({ checked: false, indeterminate: true });
        }
    }

    function renderLabel(label) {
        if (typeof label == "object") {
            return label[1];
        } else if (typeof label == "string") {
            return label;
        }
    }

    function tagRender(props) {
        return (
            <span className="ant-select-selection-item">
                <span className="ant-select-selection-item-content">{renderLabel(props.label)}</span>
                <span
                    className="ant-select-selection-item-remove"
                    style={{ userSelect: "none", verticalAlign: "0" }}
                    onClick={() => {
                        changeSelect(value.filter(item => item !== props.value));
                    }}>
                    <span role="img" className="anticon anticon-close">
                        <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="currentColor">
                            <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 00203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z"></path>
                        </svg>
                    </span>
                </span>
            </span>
        );
    }

    function allSelect() {
        allCheckedSet(prevValue => {
            valueSet(prevValue.checked ? [] : allId);
            return { checked: !prevValue.checked, indeterminate: false };
        });
    }

    return (
        <React.Fragment>
            <label className="search-label">
                {item.label}
                <ShowTooltip item={item} />
            </label>
            <Select
                mode="multiple"
                showSearch
                maxTagCount={5}
                tagRender={tagRender}
                value={value.filter(value => value)}
                optionFilterProp="children"
                onChange={value => {
                    if (value.includes("")) {
                        allSelect();
                        return;
                    }
                    changeSelect(value);
                }}>
                {list.map((item, index) => (
                    <React.Fragment key={index}>
                        {index == 0 && (
                            <Select.Option key="all" value="">
                                <Checkbox
                                    checked={allChecked.checked}
                                    indeterminate={allChecked.indeterminate}
                                    style={{ marginRight: 6 }}
                                />
                                全选
                            </Select.Option>
                        )}
                        <Select.Option key={index} value={item.id}>
                            <Checkbox checked={value.includes(item.id)} style={{ marginRight: 6 }} />
                            {item.name}
                        </Select.Option>
                    </React.Fragment>
                ))}
            </Select>
        </React.Fragment>
    );
});
const SliderRange = inject(({ store, item }) => {
    const [show, setShow] = useState(false);
    const [value, setValue] = useState([null, null]);
    let search = store.assembleSearch();
    let { max, min, maxKey, minKey, unit = "时", step, rangeType } = item;

    const onOk = useMemo(() => {
        return () => {
            store.setValue(minKey, value[0]);
            store.setValue(maxKey, value[1]);
            setShow(false);
        };
    }, [value, show]);

    const inputModeChange = (val, type) => {
        if (type === "left") {
            store.setValue(minKey, val);
            store.setValue(maxKey, value[1]);
            setValue([val, value[1]]);
        } else {
            store.setValue(minKey, value[0]);
            store.setValue(maxKey, val);
            setValue([value[0], val]);
        }
    };
    useEffect(() => {
        console.log(search, search[minKey], search[maxKey]);
        setValue([search[minKey] || null, search[maxKey] || null]);
    }, [search[maxKey], search[minKey]]);
    const handleCancel = () => {
        setShow(false);
    };
    return (
        <SearchItemRoot>
            <label className="search-item-label">
                {item.label}
                <ShowTooltip item={item} />
            </label>
            <div className="slider-range-pop">
                {rangeType === "input" ? (
                    <div className="ant-input">
                        <div className="dt-input-container">
                            <div className="dt-input-container-left">
                                <InputNumber
                                    controls={false}
                                    className="dt-input"
                                    value={value[0]}
                                    placeholder={"最小"}
                                    bordered={false}
                                    onChange={e => inputModeChange(e, "left")}
                                />
                                ～
                                <InputNumber
                                    controls={false}
                                    className="dt-input"
                                    placeholder={"最大"}
                                    value={value[1]}
                                    bordered={false}
                                    onChange={e => inputModeChange(e, "right")}
                                />
                            </div>
                            <div className="ant-input-unit">{unit}</div>
                        </div>
                    </div>
                ) : (
                    <Popconfirm
                        title={
                            <Slider
                                max={+max}
                                min={+min}
                                style={{ width: "200px", marginRight: "20px" }}
                                step={Number(step) || 1}
                                range
                                value={value}
                                onChange={e => {
                                    setValue(e);
                                }}
                            />
                        }
                        icon={null}
                        onConfirm={onOk}
                        onCancel={handleCancel}
                        okText="确认"
                        cancelText="取消"
                        overlayClassName="search-pop-confirm">
                        <div
                            className="ant-input"
                            onClick={() => {
                                setShow(true);
                            }}>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    justifyContent: "space-around",
                                    padding: "4px 11px",
                                    flex: 1,
                                }}>
                                <div>{search[minKey]}</div>～<div>{search[maxKey]}</div>
                            </div>
                            <div className="ant-input-unit">{unit}</div>
                        </div>
                    </Popconfirm>
                )}
            </div>
        </SearchItemRoot>
    );
});

const map = {
    input: InputForm,
    select: SelectForm,
    date: DateForm,
    "select-textarea": SelectTextArea,
    cascader: CascaderForm,
    "select-check": SelectForm,
    "slider-range": SliderRange,
};
const SearchConditionList = forwardRef(
    ({ showMini = true, searchKeyList, onSearch, defaultSearch, initResetClearConfig, defaultCollapsed }, ref) => {
        let [store] = useState(() => new SearchStore(initResetClearConfig));
        let history = useHistory();
        let [isMini, setMini] = useState(defaultCollapsed || false);

        useEffect(() => {
            event.on("resetStore", storeReset);
            // 取页面传值，放到useState中，可清除
            let locationState = history.location.state;
            if (locationState) {
                store.pageParam = locationState;
                Object.keys(locationState).map(key => {
                    store.setValue(key, locationState[key]);
                });
            }
            return () => {
                event.off("resetStore", storeReset);
            };
        }, []);

        useMountedOrUpdateEffectFn(
            isMounted => {
                let defaultSearchParams = defaultSearch.toJS();
                Object.entries(store.getUrlSearchParam()).map(([key, value]) => {
                    store.setValue(key, decodeURIComponent(value));
                });
                store.setDefaultSearchParam(defaultSearchParams);
                executeSearch(
                    isMounted ? SearchSourceType.DEFAULT_SEARCH : SearchSourceType.CHANGE_IMMUTABLE_SEARCH_CHANGE,
                );
            },
            [defaultSearch],
        );

        useImperativeHandle(ref, () => ({
            getSearchCondition: getSearchCondition,
            setDefaultSearchCondition: setDefaultSearchCondition,
            setSearchCondition: setSearchCondition,
            setResetClearConfig: setResetClearConfig,
        }));

        /**
         * 触发一次搜索。
         * @param   {SearchSourceType} searchSource
         */
        const executeSearch = function (searchSource) {
            switch (searchSource) {
                case SearchSourceType.RESET_BUTTON: //重置按钮
                    store.reset();
                    break;
                default:
                    break;
            }
            let searchParma = store.assembleSearch(true);
            onSearch(searchParma, searchSource);
        };

        function storeReset() {
            store.reset();
        }
        function getSearchCondition() {
            return store.assembleSearch(true);
        }

        /**
         * 设置默认搜索条件
         * @param defaultSearchParam 默认搜索参数
         * @param immediately 是否立即触发搜索
         */
        function setDefaultSearchCondition(defaultSearchParam, immediately = true) {
            store.setDefaultSearchParam(defaultSearchParam);
            if (immediately) {
                executeSearch(SearchSourceType.METHOD_SET_SEARCH_PARAM);
            }
        }
        /**
         * 设置表单搜索条件
         * @param searchParam 指表单搜索参数
         * @param immediately 是否触发搜索
         */
        function setSearchCondition(searchParam, immediately = true) {
            store.setSearchParam(searchParam);
            if (immediately) {
                executeSearch(SearchSourceType.METHOD_SET_DEFAULT_SEARCH_PARAM);
            }
        }

        /**
         * 设置默认清空开关控制
         * @param {"defaultSearchParamClearable"|"urlSearchParam"} key
         * @param {boolean} value
         */
        function setResetClearConfig(key, value) {
            store.setResetClearConfigByKeyValue(key, value);
        }

        const assembleCommonSearchControl = (item, isMini) => {
            let Control = map[item.type];
            if (item.isTwoSelect && ["select-check", "select"].includes(item.type)) {
                Control = MultiSelectForm;
            }
            if (Control) {
                let colProps = item.colProps || { span: 24, md: 12, xl: 8, xxl: 6 };
                return (
                    <Col {...colProps} className={classNames({ "mini-search": isMini })}>
                        <Control
                            item={item}
                            searchFc={() => executeSearch(SearchSourceType.SINGLE_CONTROL)}
                            className=""
                        />
                    </Col>
                );
            }
            return <></>;
        };
        const lessSearchControl = useMemo(() => {
            return searchKeyList.filter((_, index) => index < 3).map(item => assembleCommonSearchControl(item, false));
        }, [searchKeyList]);

        const moreSearchControl = useMemo(() => {
            return searchKeyList
                .filter((_, index) => index >= 3)
                .map(item => assembleCommonSearchControl(item, isMini));
        }, [searchKeyList, isMini]);

        return (
            <Provider store={store}>
                <>
                    <div className={`full-search-v2 ${isMini ? "mini-search" : "full-search"}`}>
                        {/* <Row gutter={[16, 10]} style={{ paddingRight: "20px" }}> */}
                        <Row gutter={[16, 10]}>
                            {/* <Row > */}
                            {lessSearchControl}
                            {moreSearchControl}
                            <Col span={24} md={12} xl={8} xxl={6} className="search-btns">
                                <Space>
                                    <Button
                                        type="primary"
                                        ghost
                                        onClick={() => {
                                            executeSearch(SearchSourceType.SEARCH_BUTTON);
                                        }}
                                        onKeyDown={event => {
                                            if (event.keyCode === 13) {
                                                event.preventDefault();
                                            }
                                        }}>
                                        搜索
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            executeSearch(SearchSourceType.RESET_BUTTON);
                                        }}>
                                        重置
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </div>
                    {showMini && (
                        <div
                            className={`change-type ${isMini}`}
                            onClick={() => {
                                setMini(!isMini);
                                event.emit("window.resize");
                            }}
                        />
                    )}
                </>
            </Provider>
        );
    },
);

export default observer(SearchConditionList);
